import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ReplayIcon from '@mui/icons-material/Replay';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { api, getApiErrorMessage } from '../api';

const MAX_BATCH_SIZE = 30;
const MAX_SOURCE_BYTES = 100 * 1024 * 1024;
const IMAGE_VARIANTS = { thumb: 240, medium: 640, full: 1600 };
const WEBP_QUALITY = 0.76;
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);

function statusLabel(status) {
  return {
    queued: 'Queued',
    optimizing: 'Optimizing…',
    uploading: 'Uploading…',
    uploaded: 'Uploaded',
    failed: 'Upload failed'
  }[status] || status;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('This photo could not be decoded by the browser.'));
    };
    image.src = url;
  });
}

async function optimizeImage(file) {
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('Photo is larger than 100 MB.');
  }
  if (!ACCEPTED_TYPES.has(file.type.toLowerCase())) {
    throw new Error('This file type is not supported. Upload images only.');
  }

  const image = await loadImage(file);
  const variants = {};
  for (const [name, maxEdge] of Object.entries(IMAGE_VARIANTS)) {
    const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(value => value ? resolve(value) : reject(new Error('Photo optimization failed.')), 'image/webp', WEBP_QUALITY);
    });
    canvas.width = 1;
    canvas.height = 1;
    variants[name] = { blob, width, height };
  }
  return { ...variants.full, variants };
}

function UploadPhotos() {
  const inputRef = useRef(null);
  const batchRef = useRef(null);
  const photosRef = useRef([]);
  const [batch, setBatch] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmedCount, setConfirmedCount] = useState(null);
  const [selectedDelete, setSelectedDelete] = useState(new Set());
  const [deleteSelectedOpen, setDeleteSelectedOpen] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);

  const updatePhoto = useCallback((id, changes) => {
    setPhotos(current => current.map(photo => photo.id === id ? { ...photo, ...changes } : photo));
  }, []);

  const loadActiveBatch = useCallback(async () => {
    try {
      const response = await api.get('/v1/upload-batches/active');
      const activeBatch = response.data;
      batchRef.current = activeBatch;
      setBatch(activeBatch);
      const existing = await Promise.all((activeBatch.assets || []).map(async asset => {
        try {
          const { data } = await api.get(`/v1/assets/${asset.id}/download-url?variant=medium`);
          return { id: asset.id, fileName: asset.originalFileName, status: 'uploaded', previewUrl: data.downloadUrl, asset };
        } catch {
          return { id: asset.id, fileName: asset.originalFileName, status: 'uploaded', asset };
        }
      }));
      setPhotos(existing);
    } catch (err) {
      if (err?.response?.status !== 404) setError(getApiErrorMessage(err, 'Unable to load upload batch.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    loadActiveBatch();
    return () => photosRef.current.forEach(photo => photo.previewUrl && URL.revokeObjectURL(photo.previewUrl));
  }, [loadActiveBatch]);

  const ensureBatch = async () => {
    if (batchRef.current) return batchRef.current;
    const { data } = await api.post('/v1/upload-batches');
    batchRef.current = data;
    setBatch(data);
    return data;
  };

  const processPhoto = async (photo, batchData) => {
    updatePhoto(photo.id, { status: 'optimizing', error: '' });
    const optimized = await optimizeImage(photo.file);
    updatePhoto(photo.id, { status: 'uploading', previewUrl: URL.createObjectURL(optimized.variants.medium.blob), width: optimized.width, height: optimized.height });

    const metadata = {
      originalFileName: photo.file.name,
      sourceContentType: photo.file.type,
      sourceByteSize: photo.file.size,
      thumbnailByteSize: optimized.variants.thumb.blob.size,
      mediumByteSize: optimized.variants.medium.blob.size,
      fullByteSize: optimized.variants.full.blob.size,
      width: optimized.width,
      height: optimized.height,
      displayOrder: photo.displayOrder
    };
    const { data: authorization } = await api.post(
      `/v1/upload-batches/${batchData.id}/assets/upload-url`, metadata);
    await Promise.all(authorization.uploads.map(upload => axios.put(upload.uploadUrl, optimized.variants[upload.variant].blob, {
      headers: { 'Content-Type': authorization.contentType }
    })));
    const { data: asset } = await api.post(
      `/v1/upload-batches/${batchData.id}/assets/${authorization.assetId}/complete`, metadata);
    updatePhoto(photo.id, { status: 'uploaded', asset, previewUrl: URL.createObjectURL(optimized.variants.medium.blob) });
  };

  const handleFiles = async event => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (selected.length === 0) return;

    const remaining = MAX_BATCH_SIZE - photos.filter(photo => photo.status !== 'failed').length;
    const accepted = selected.slice(0, Math.max(0, remaining));
    if (accepted.length < selected.length) {
      setNotice(`${selected.length - accepted.length} photo${selected.length - accepted.length === 1 ? '' : 's'} excluded. Batches support up to 30 photos.`);
    } else {
      setNotice('');
    }
    if (accepted.length === 0) return;

    const existingCount = photos.filter(photo => photo.status !== 'failed').length;
    const newPhotos = accepted.map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${Math.random()}`,
      fileName: file.name,
      file,
      displayOrder: existingCount + index,
      status: 'queued',
      previewUrl: URL.createObjectURL(file)
    }));
    setPhotos(current => [...current, ...newPhotos]);
    setProcessing(true);
    setError('');
    try {
      const activeBatch = await ensureBatch();
      for (const photo of newPhotos) {
        try {
          await processPhoto(photo, activeBatch);
        } catch (err) {
          updatePhoto(photo.id, { status: 'failed', error: getApiErrorMessage(err, 'Photo upload failed.') });
        }
      }
      const refreshed = await api.get('/v1/upload-batches/active');
      batchRef.current = refreshed.data;
      setBatch(refreshed.data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to start upload batch.'));
    } finally {
      setProcessing(false);
    }
  };

  const retryPhoto = async photo => {
    setProcessing(true);
    try {
      const activeBatch = await ensureBatch();
      await processPhoto(photo, activeBatch);
      setNotice('Photo uploaded.');
    } catch (err) {
      updatePhoto(photo.id, { status: 'failed', error: getApiErrorMessage(err, 'Photo upload failed.') });
    } finally {
      setProcessing(false);
    }
  };

  const removePhoto = photo => {
    if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
    setPhotos(current => current.filter(currentPhoto => currentPhoto.id !== photo.id));
  };

  const toggleDeleteSelection = photoId => {
    setSelectedDelete(current => {
      const next = new Set(current);
      if (next.has(photoId)) next.delete(photoId); else next.add(photoId);
      return next;
    });
  };

  const deleteSelectedPhotos = async () => {
    const deletingAll = selectedDelete.size === 0;
    const targets = photos.filter(photo => photo.status === 'uploaded' && photo.asset?.id && (deletingAll || selectedDelete.has(photo.id)));
    setDeletingSelected(true);
    try {
      await Promise.all(targets.map(photo => api.delete(`/v1/assets/${photo.asset.id}`)));
      targets.forEach(photo => photo.previewUrl && URL.revokeObjectURL(photo.previewUrl));
      setPhotos(current => deletingAll ? [] : current.filter(photo => !selectedDelete.has(photo.id)));
      setSelectedDelete(new Set());
      setDeleteSelectedOpen(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to delete selected photos.'));
    } finally {
      setDeletingSelected(false);
    }
  };

  const confirmBatch = async () => {
    if (!batchRef.current) return;
    const selectedUploadIds = new Set(selectedDelete);
    const uploadTargets = photos.filter(photo => photo.status === 'uploaded' && photo.asset?.id && (selectedUploadIds.size === 0 || selectedUploadIds.has(photo.id)));
    if (uploadTargets.length === 0) return;
    setProcessing(true);
    try {
      await api.post(`/v1/upload-batches/${batchRef.current.id}/confirm`, { assetIds: selectedUploadIds.size > 0 ? uploadTargets.map(photo => photo.asset.id) : [] });
      const count = uploadTargets.length;
      setConfirmedCount(count);
      setNotice(`${count} photo${count === 1 ? '' : 's'} uploaded.`);
      uploadTargets.forEach(photo => photo.previewUrl && URL.revokeObjectURL(photo.previewUrl));
      setPhotos(current => current.filter(photo => !uploadTargets.some(target => target.id === photo.id)));
      setSelectedDelete(new Set());
      try {
        const refreshed = await api.get('/v1/upload-batches/active');
        batchRef.current = refreshed.data;
        setBatch(refreshed.data);
      } catch (err) {
        if (err?.response?.status === 404) {
          setBatch(null);
          batchRef.current = null;
        } else {
          throw err;
        }
      }
      window.dispatchEvent(new Event('indexing:changed'));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to confirm upload batch.'));
    } finally {
      setProcessing(false);
    }
  };

  const uploadedCount = photos.filter(photo => photo.status === 'uploaded').length;
  const failedCount = photos.filter(photo => photo.status === 'failed').length;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <main className="page-shell">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4">Upload Photos</Typography>
          <Typography color="text.secondary">Upload up to 30 photos, then confirm them for Indexing.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" component="label" startIcon={<AddIcon />} disabled={processing || uploadedCount >= MAX_BATCH_SIZE} sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>
            Choose Photos
            <input ref={inputRef} hidden multiple type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={handleFiles} />
          </Button>
          <Button variant="outlined" component="label" startIcon={<PhotoCameraIcon />} disabled={processing || uploadedCount >= MAX_BATCH_SIZE} sx={{ display: 'inline-flex', alignItems: 'center', color: '#222', borderColor: '#222', fontWeight: 'bold', '&:hover': { borderColor: '#333', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
            Take Photo
            <input hidden type="file" accept="image/*" capture="environment" onChange={handleFiles} />
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice} {confirmedCount !== null && <Button component={Link} to="/indexing" endIcon={<ArrowForwardIcon />} sx={{ ml: 1, color: '#222', fontWeight: 'bold', textDecoration: 'underline', textTransform: 'none', '&:hover': { color: '#000', backgroundColor: 'transparent', textDecoration: 'underline' } }}>Go to Indexing</Button>}</Alert>}
      {failedCount > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{failedCount} photo{failedCount === 1 ? '' : 's'} failed. Retry while this page remains open, or remove and select again.</Alert>}

      {processing && <LinearProgress sx={{ mb: 2 }} />}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Photos ({photos.length}/{MAX_BATCH_SIZE})</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {photos.length > 0 && <Button variant="contained" startIcon={<DeleteForeverIcon />} onClick={() => setDeleteSelectedOpen(true)} disabled={processing || deletingSelected} sx={{ minHeight: 48, px: 2, backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{selectedDelete.size > 0 ? `Delete selected (${selectedDelete.size})` : 'Abandon'}</Button>}
            </Box>
          </Box>

          {photos.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography color="text.secondary">Choose photos to begin this batch.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 2 }}>
              {photos.map(photo => (
                <Box key={photo.id} sx={{ position: 'relative', minWidth: 0 }}>
                  <Box onClick={() => photo.status === 'uploaded' && toggleDeleteSelection(photo.id)} sx={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: 1, overflow: 'hidden', backgroundColor: '#f2f2f2', border: selectedDelete.has(photo.id) ? '3px solid #222' : photo.status === 'failed' ? '2px solid #d32f2f' : '1px solid #ddd', cursor: photo.status === 'uploaded' ? 'pointer' : 'default' }}>
                    {photo.previewUrl ? <Box component="img" src={photo.previewUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UploadFileIcon color="disabled" /></Box>}
                    {photo.status === 'uploaded' && <Checkbox checked={selectedDelete.has(photo.id)} onClick={event => event.stopPropagation()} onChange={() => toggleDeleteSelection(photo.id)} disabled={processing || deletingSelected} sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, p: 0.25, color: '#222', '&.Mui-checked': { color: '#222' }, backgroundColor: '#fff', borderRadius: 1, '&:hover': { backgroundColor: '#fff' } }} />}
                    {selectedDelete.has(photo.id) && <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.14)', pointerEvents: 'none' }}><CheckCircleIcon sx={{ color: '#fff', fontSize: 28, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} /></Box>}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" noWrap title={photo.fileName}>{photo.fileName}</Typography>
                    {photo.status === 'uploaded' && <CheckCircleIcon color="success" fontSize="small" />}
                    {photo.status === 'failed' && <ErrorOutlineIcon color="error" fontSize="small" />}
                  </Box>
                  <Typography variant="caption" color={photo.status === 'failed' ? 'error' : 'text.secondary'}>{statusLabel(photo.status)}</Typography>
                  {photo.status === 'failed' && <Button size="small" startIcon={<ReplayIcon />} onClick={() => retryPhoto(photo)} disabled={processing}>Retry</Button>}
                  {(photo.status === 'failed' || photo.status === 'queued') && <Button size="small" startIcon={<CloseIcon />} onClick={() => removePhoto(photo)} disabled={processing}>Remove</Button>}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {batch && batch.status !== 'Confirmed' && <Box sx={{ position: 'sticky', bottom: 16, mt: 2, display: 'flex', justifyContent: 'flex-end', zIndex: 2, py: 1, px: 1, backgroundColor: 'rgba(250, 249, 246, 0.92)', borderRadius: 1 }}><Button variant="contained" startIcon={<UploadFileIcon />} onClick={confirmBatch} disabled={processing || uploadedCount === 0} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{selectedDelete.size > 0 ? `Upload selected (${selectedDelete.size})` : 'Upload'}</Button></Box>}

      <Dialog open={deleteSelectedOpen} onClose={() => !deletingSelected && setDeleteSelectedOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{selectedDelete.size > 0 ? 'Delete selected photos?' : 'Abandon upload batch?'}</DialogTitle>
        <DialogContent>This permanently deletes {selectedDelete.size > 0 ? selectedDelete.size : photos.length} photo{(selectedDelete.size > 0 ? selectedDelete.size : photos.length) === 1 ? '' : 's'} from this upload batch and storage.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSelectedOpen(false)} disabled={deletingSelected} sx={{ color: '#222' }}>Cancel</Button>
          <Button onClick={deleteSelectedPhotos} variant="contained" disabled={deletingSelected} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{deletingSelected ? 'Working…' : selectedDelete.size > 0 ? 'Delete photos' : 'Abandon batch'}</Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}

export default UploadPhotos;
export { optimizeImage };
